'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { articles, CATEGORY_LABELS, type ArticleCategory } from '@/data/articles';
import { searchLearn, type LearnSearchItem, type LearnItemType } from '@/lib/learnSearch';

// ── Icons ─────────────────────────────────────────────────────────────────────

function BugIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="14" cy="17" rx="6" ry="8" />
      <path d="M14 9a3 3 0 0 1 0-6" />
      <path d="M14 9a3 3 0 0 0 0-6" />
      <path d="M8 12.5L3 9.5M8 17H3M8 21.5L3 24.5" />
      <path d="M20 12.5l5-3M20 17h5M20 21.5l5 3" />
    </svg>
  );
}

function SeedlingIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22V12" />
      <path d="M12 12C10 12 6 11 6 7C6 3.5 9 3 10.5 4.5C11.5 5.5 12 9 12 12Z" />
      <path d="M12 16C14 16 18 15 18 11C18 7.5 15 7 13.5 8.5C12.5 9.5 12 13 12 16Z" />
    </svg>
  );
}

function LeafIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 18C2 18 4 10 12 7C18 5 20 5 20 5C20 5 20 11 17 14C14 17 8 18 2 18Z" />
      <path d="M2 18L9 11" />
    </svg>
  );
}

function EarthIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12C3 12 6 14 9 12C12 10 12 7 15 7C18 7 21 10 21 12" />
      <path d="M6 18C6 18 8 16 12 16C16 16 18 18 18 18" />
      <path d="M12 3V7" />
    </svg>
  );
}

function FlowerIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2.5" />
      <path d="M12 4C12 4 10 7 12 9.5C14 7 12 4 12 4Z" />
      <path d="M12 14.5C12 14.5 14 17 12 20C10 17 12 14.5 12 14.5Z" />
      <path d="M4 12C4 12 7 10 9.5 12C7 14 4 12 4 12Z" />
      <path d="M14.5 12C14.5 12 17 14 20 12C17 10 14.5 12 14.5 12Z" />
      <path d="M6 6C6 6 7.5 9 10 9.5C9.5 7 6 6 6 6Z" />
      <path d="M14 14.5C14 14.5 17 16 18 18C16 17 14 14.5 14 14.5Z" />
      <path d="M18 6C18 6 16 9 14 9.5C14.5 7 18 6 18 6Z" />
      <path d="M10 14.5C10 14.5 7 16 6 18C8 17 10 14.5 10 14.5Z" />
    </svg>
  );
}

function FlaskIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6M9 3v7L4 18a1 1 0 0 0 .9 1.5h14.2A1 1 0 0 0 20 18L15 10V3" />
      <path d="M7.5 16.5h9" />
    </svg>
  );
}

function ButterflyIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 12C12 12 8 8 4 9C2 9.5 2 12 4 13C6 14 10 13 12 12Z" />
      <path d="M12 12C12 12 16 8 20 9C22 9.5 22 12 20 13C18 14 14 13 12 12Z" />
      <path d="M12 12C12 12 8 15 5 15C3 15 2 13 4 12" />
      <path d="M12 12C12 12 16 15 19 15C21 15 22 13 20 12" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <path d="M12 13V19" />
    </svg>
  );
}

function BottleIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6M9 3v3L6 10v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V10L15 6V3" />
      <path d="M6 14h12" />
      <path d="M9 17.5h6" />
    </svg>
  );
}

function GridIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function HandsIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 16.5C4 16.5 4 13 5 11C6 9 8 9 9 10V6.5C9 5.7 9.7 5 10.5 5C11.3 5 12 5.7 12 6.5V11" />
      <path d="M12 11V8.5C12 7.7 12.7 7 13.5 7C14.3 7 15 7.7 15 8.5V11" />
      <path d="M15 11V9.5C15 8.7 15.7 8 16.5 8C17.3 8 18 8.7 18 9.5V14C18 17.3 15.3 20 12 20H10C8.4 20 7 19.4 6 18.4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="6" />
      <path d="M7 4v3l2 2" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

// ── Type badge ────────────────────────────────────────────────────────────────

const TYPE_BADGE_STYLES: Record<LearnItemType, string> = {
  article:    'text-deep-green bg-light-sage border-[#C5D9B8]',
  pest:       'text-red-700 bg-red-50 border-red-200',
  remedy:     'text-purple-700 bg-purple-50 border-purple-200',
  beneficial: 'text-blue-700 bg-blue-50 border-blue-200',
  guide:      'text-amber-800 bg-amber-50 border-amber-200',
  tool:       'text-slate-700 bg-slate-100 border-slate-200',
};

function TypeBadge({ type, label }: { type: LearnItemType; label: string }) {
  return (
    <span className={`font-body text-[11px] px-2.5 py-0.5 rounded-full border ${TYPE_BADGE_STYLES[type]}`}>
      {label}
    </span>
  );
}

// ── Search result item ────────────────────────────────────────────────────────

function SearchResultItem({ item }: { item: LearnSearchItem }) {
  return (
    <Link
      href={item.href}
      className="group flex flex-col gap-1.5 bg-white rounded-xl border border-sand p-4
                 hover:border-sage transition-colors"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <TypeBadge type={item.type} label={item.typeLabel} />
        <span className="font-body text-[11px] text-sage">{item.category}</span>
      </div>
      <h3 className="font-body text-[15px] font-semibold text-dark-text leading-snug
                     group-hover:text-deep-green transition-colors">
        {item.title}
      </h3>
      <p className="font-body text-[13px] text-sage leading-relaxed line-clamp-2">
        {item.description}
      </p>
    </Link>
  );
}

// ── Article card ──────────────────────────────────────────────────────────────

function ArticleCard({ article }: { article: typeof articles[0] }) {
  return (
    <Link
      href={`/learn/articles/${article.slug}`}
      className="group bg-white rounded-xl border border-sand p-5 flex flex-col gap-3
                 hover:border-sage transition-colors"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-body text-[11px] text-sage bg-light-sage border border-[#C5D9B8]
                         px-2.5 py-0.5 rounded-full">
          {CATEGORY_LABELS[article.category]}
        </span>
        {article.tier === 'premium' && (
          <span className="font-body text-[11px] text-amber-700 bg-amber-50 border border-amber-200
                           px-2.5 py-0.5 rounded-full">
            Pro
          </span>
        )}
      </div>
      <div className="flex-1">
        <h3 className="font-body text-[15px] font-semibold text-dark-text leading-snug
                       group-hover:text-deep-green transition-colors">
          {article.title}
        </h3>
        <p className="font-body text-[13px] text-sage mt-1.5 leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>
      </div>
      <p className="font-body text-[12px] text-sage flex items-center gap-1">
        <ClockIcon />{article.readingMinutes} min read
      </p>
    </Link>
  );
}

// ── Type filter config ────────────────────────────────────────────────────────

const TYPE_FILTERS: { value: LearnItemType | ''; label: string }[] = [
  { value: '',            label: 'All' },
  { value: 'article',    label: 'Articles' },
  { value: 'pest',       label: 'Pests' },
  { value: 'remedy',     label: 'Remedies' },
  { value: 'beneficial', label: 'Beneficial' },
  { value: 'guide',      label: 'Guides' },
  { value: 'tool',       label: 'Tools' },
];

const SUGGESTION_QUERIES = ['compost', 'tomato', 'powdery mildew', 'seed starting', 'native plants', 'fruit trees'];

// ── Article category filter ───────────────────────────────────────────────────

const ALL_CATEGORIES: ArticleCategory[] = [
  'getting-started',
  'soil-and-compost',
  'companion-planting',
  'pest-and-disease',
  'watering-and-feeding',
  'seasonal-growing',
  'harvesting-and-storage',
  'fruit-trees',
];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LearnPage() {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<LearnItemType | ''>('');
  const [activeCategory, setActiveCategory] = useState<ArticleCategory | 'all'>('all');

  const trimmedQuery = query.trim();
  const showSearch = trimmedQuery.length > 0;

  const searchResults = useMemo(
    () => searchLearn(trimmedQuery, { type: typeFilter, limit: 40 }),
    [trimmedQuery, typeFilter],
  );

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter(a => a.category === activeCategory);

  const featured = articles.find(a => a.tier === 'free' && a.category === 'getting-started');

  return (
    <div className="space-y-8 max-w-3xl">

      {/* Header */}
      <div>
        <h1 className="font-heading text-[28px] text-dark-text leading-tight">Learn</h1>
        <p className="font-body text-[16px] text-sage mt-1">
          Guides and organic gardening wisdom
        </p>
      </div>

      {/* Search input */}
      <div className="relative">
        <label htmlFor="learn-search" className="sr-only">
          Search articles, pests, remedies, and tools
        </label>
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sage pointer-events-none">
          <SearchIcon />
        </span>
        <input
          id="learn-search"
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search articles, pests, remedies, tools..."
          className="w-full font-body text-[15px] text-dark-text bg-white border border-sand
                     rounded-xl pl-10 pr-4 py-3 placeholder:text-sage/60
                     focus:outline-none focus:border-sage transition-colors"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setTypeFilter(''); }}
            aria-label="Clear search"
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sage hover:text-dark-text
                       transition-colors text-lg leading-none"
          >
            &times;
          </button>
        )}
      </div>

      {showSearch ? (
        /* ── Search results ── */
        <div className="space-y-4">

          {/* Type filter pills */}
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by type">
            {TYPE_FILTERS.map(({ value, label }) => (
              <button
                key={value || 'all'}
                onClick={() => setTypeFilter(value)}
                className={[
                  'font-body text-[13px] px-3.5 py-1.5 rounded-full border transition-all',
                  typeFilter === value
                    ? 'bg-deep-green text-white border-deep-green'
                    : 'bg-white text-dark-text border-sand hover:border-sage',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>

          {searchResults.length > 0 ? (
            <>
              <p className="font-body text-[13px] text-sage">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{trimmedQuery}&rdquo;
              </p>
              <div className="flex flex-col gap-3">
                {searchResults.map(item => (
                  <SearchResultItem key={item.id} item={item} />
                ))}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl border border-sand p-6 text-center space-y-3">
              <p className="font-body text-[15px] text-dark-text">
                No exact matches yet.
              </p>
              <p className="font-body text-[13px] text-sage leading-relaxed">
                Try searching for a plant problem, soil topic, pest, remedy, or tool.
              </p>
              <div className="flex flex-wrap justify-center gap-2 pt-1">
                {SUGGESTION_QUERIES.map(s => (
                  <button
                    key={s}
                    onClick={() => setQuery(s)}
                    className="font-body text-[12px] text-deep-green bg-light-sage border
                               border-[#C5D9B8] px-3 py-1 rounded-full hover:border-sage
                               transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── Default content ── */
        <>
          {/* Featured article */}
          {featured && (
            <Link
              href={`/learn/articles/${featured.slug}`}
              className="group block bg-deep-green rounded-xl p-6 hover:opacity-95 transition-opacity"
            >
              <span className="inline-block font-body text-[11px] font-semibold text-white/70
                               bg-white/15 px-2.5 py-0.5 rounded-full mb-3">
                Start here
              </span>
              <h2 className="font-heading text-[22px] text-white leading-tight">
                {featured.title}
              </h2>
              <p className="font-body text-[14px] text-white/75 mt-2 leading-relaxed max-w-md">
                {featured.excerpt}
              </p>
              <span className="inline-flex items-center gap-1 mt-4 font-body text-[13px]
                               font-semibold text-white group-hover:underline">
                Read article &rarr;
              </span>
            </Link>
          )}

          {/* Quick tools intro */}
          <div>
            <p className="font-body text-[14px] text-sage leading-relaxed">
              Identify problems, understand what&rsquo;s happening, and fix them naturally.
            </p>
          </div>

          {/* Quick tools row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

            <Link href="/learn/pests"
              className="group flex items-start gap-3 bg-white rounded-xl border border-sand p-5
                         hover:border-sage transition-colors">
              <span className="text-deep-green flex-shrink-0 mt-0.5">
                <BugIcon size={22} />
              </span>
              <div>
                <h2 className="font-heading text-[16px] text-dark-text group-hover:text-deep-green
                               transition-colors leading-snug">
                  Pest &amp; Disease Identifier
                </h2>
                <p className="font-body text-[13px] text-sage mt-1 leading-relaxed">
                  Spot symptoms, identify the culprit, find organic solutions.
                </p>
                <span className="inline-block mt-2 font-body text-[13px] font-semibold text-deep-green
                                 group-hover:underline">
                  Identify &rarr;
                </span>
              </div>
            </Link>

            <Link href="/learn/beneficial"
              className="group flex items-start gap-3 bg-white rounded-xl border border-sand p-5
                         hover:border-sage transition-colors">
              <span className="text-[#6B8F6E] flex-shrink-0 mt-0.5">
                <ButterflyIcon size={22} />
              </span>
              <div>
                <h2 className="font-heading text-[16px] text-dark-text group-hover:text-deep-green
                               transition-colors leading-snug">
                  Garden Allies
                </h2>
                <p className="font-body text-[13px] text-sage mt-1 leading-relaxed">
                  Meet the allies that control pests and pollinate your garden.
                </p>
                <span className="inline-block mt-2 font-body text-[13px] font-semibold text-deep-green
                                 group-hover:underline">
                  Explore &rarr;
                </span>
              </div>
            </Link>

            <Link href="/learn/remedies"
              className="group flex items-start gap-3 bg-white rounded-xl border border-sand p-5
                         hover:border-sage transition-colors">
              <span className="text-[#7C5CBF] flex-shrink-0 mt-0.5">
                <BottleIcon size={22} />
              </span>
              <div>
                <h2 className="font-heading text-[16px] text-dark-text group-hover:text-deep-green
                               transition-colors leading-snug">
                  Garden Remedies
                </h2>
                <p className="font-body text-[13px] text-sage mt-1 leading-relaxed">
                  Organic sprays, soils, and treatments - all wildlife-friendly.
                </p>
                <span className="inline-block mt-2 font-body text-[13px] font-semibold text-deep-green
                                 group-hover:underline">
                  Browse remedies &rarr;
                </span>
              </div>
            </Link>

          </div>

          {/* Deep Guides */}
          <div className="space-y-3">
            <h2 className="font-heading text-[20px] text-dark-text">Deep Guides</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

              {([
                { href: '/learn/beginners-path', Icon: SeedlingIcon, color: 'text-deep-green',
                  title: "Beginner's Path",
                  desc: 'Start here. A season-by-season guide to your first garden, built on patience, observation, and good soil.',
                  cta: 'Begin your journey →' },
                { href: '/learn/permaculture', Icon: LeafIcon, color: 'text-deep-green',
                  title: 'Permaculture Principles',
                  desc: 'The 12 principles that transform a garden into a living ecosystem. Design with nature, not against it.',
                  cta: 'Explore principles →' },
                { href: '/learn/soil', Icon: EarthIcon, color: 'text-[#8B6F47]',
                  title: 'Soil Health',
                  desc: 'Healthy soil is not dirt. It is a living community. Learn to feed your soil and it will feed everything else.',
                  cta: 'Understand your soil →' },
                { href: '/learn/native-plants', Icon: FlowerIcon, color: 'text-[#B85C8A]',
                  title: 'Native Plants',
                  desc: 'Plants that belong here. Why native species matter for pollinators, soil, water, and the broader web of life.',
                  cta: 'Explore natives →' },
                { href: '/learn/guilds', Icon: GridIcon, color: 'text-[#7C5CBF]',
                  title: 'Interplanting and Guilds',
                  desc: 'Beyond companion planting. How to design polyculture plant communities that support and protect each other.',
                  cta: 'Design a guild →' },
                { href: '/learn/indigenous-land-care', Icon: HandsIcon, color: 'text-[#C2703E]',
                  title: 'Indigenous Land Care',
                  desc: 'Thousands of years of reciprocal land stewardship. Principles and practices that predate modern agriculture.',
                  cta: 'Learn more →' },
                { href: '/learn/natural-inputs', Icon: FlaskIcon, color: 'text-[#2E7D32]',
                  title: 'Natural Inputs Guide',
                  desc: 'What to feed your soil, how to manage pests without harm, and why the inputs you choose matter.',
                  cta: 'Read the guide →' },
              ] as const).map(({ href, Icon, color, title, desc, cta }) => (
                <Link key={href} href={href}
                  className="group flex items-start gap-4 bg-white rounded-xl border border-sand p-5
                             hover:border-sage transition-colors">
                  <span className={`${color} flex-shrink-0 mt-0.5`}><Icon size={22} /></span>
                  <div>
                    <h3 className="font-heading text-[16px] text-dark-text group-hover:text-deep-green
                                   transition-colors leading-snug">
                      {title}
                    </h3>
                    <p className="font-body text-[13px] text-sage mt-1 leading-relaxed">{desc}</p>
                    <span className="inline-block mt-2 font-body text-[13px] font-semibold text-deep-green
                                     group-hover:underline">
                      {cta}
                    </span>
                  </div>
                </Link>
              ))}

            </div>
          </div>

          {/* Category filter pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory('all')}
              className={[
                'font-body text-[13px] px-3.5 py-1.5 rounded-full border transition-all',
                activeCategory === 'all'
                  ? 'bg-deep-green text-white border-deep-green'
                  : 'bg-white text-dark-text border-sand hover:border-sage',
              ].join(' ')}
            >
              All articles
            </button>
            {ALL_CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={[
                  'font-body text-[13px] px-3.5 py-1.5 rounded-full border transition-all',
                  activeCategory === cat
                    ? 'bg-deep-green text-white border-deep-green'
                    : 'bg-white text-dark-text border-sand hover:border-sage',
                ].join(' ')}
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>

          {/* Article grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filteredArticles.map(article => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>

          {/* Philosophy link */}
          <div className="pt-2 pb-1">
            <Link
              href="/philosophy"
              className="font-body text-[13px] text-sage hover:text-deep-green transition-colors"
            >
              Curious about the values behind Garden? Read our philosophy &rarr;
            </Link>
          </div>
        </>
      )}

    </div>
  );
}
